import {
  largeDesktopWidth,
  mobileWidthMax,
  white,
  desktopWidth,
  mediumDesktopExactWidth,
  smallDesktopWidth,
  tabletWidth,
  accessibilityColors,
} from '@edulastic/colors'
import { segmentApi } from '@edulastic/api'
import { PropTypes } from 'prop-types'
import React from 'react'
import { Tooltip } from 'antd'
import { withRouter } from 'react-router'
import styled from 'styled-components'
import ScreenReaderOnly from '../../../../common/components/ScreenReaderOnly'

const HeaderTabs = ({
  id,
  key,
  onClickHandler,
  to,
  disabled,
  dataCy,
  isActive,
  icon,
  linkLabel,
  history,
  hideIcon,
  activeStyle,
  isNew,
  mediumDesktopWidth = mediumDesktopExactWidth,
  tabDisabledTitle,
  tabIcon = null,
  segmentEventData = {},
  onArrowNavigation,
  onKeyDown: externalOnKeyDown,
  ...restProps
}) => {
  const handleOnClick = () => {
    if (disabled) return
    if (to && to !== '#') {
      // we are sending a place to route
      history.push(to)
    } else if (typeof onClickHandler === 'function') {
      // call the onClickHandler passed from component
      onClickHandler()
    }
    if (segmentEventData?.eventName) {
      segmentApi.genericEventTrack(segmentEventData.eventName, {
        ...(segmentEventData?.eventData || {}),
      })
    }
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (typeof onClickHandler === 'function') {
        handleOnClick()
      }
      return
    }
    if (typeof onArrowNavigation === 'function') {
      if (event.key === 'ArrowLeft') {
        onArrowNavigation('left')
      }
      if (event.key === 'ArrowRight') {
        onArrowNavigation('right')
      }
    }
    if (typeof externalOnKeyDown === 'function') {
      externalOnKeyDown(event)
    }
  }

  return (
    <Tooltip title={disabled ? tabDisabledTitle : null}>
      <TabWrapper
        id={id || `${dataCy}-tab-control`}
        key={key}
        onClick={handleOnClick}
        disabled={disabled}
        data-cy={dataCy}
        tabIcon={tabIcon}
        tabIndex={disabled || !isActive ? -1 : 0}
        role="tab"
        aria-selected={isActive}
        aria-controls={`${dataCy}-panel`}
        className="header-tab"
        activeStyle={activeStyle}
        isActive={isActive}
        hasIcon={!!icon}
        hideIcon={hideIcon}
        mediumDesktopWidth={mediumDesktopWidth}
        onKeyDown={handleKeyPress}
        aria-disabled={disabled ? 'true' : 'false'}
        aria-describedby={disabled ? id || `${dataCy}-tab-control` : null}
        onFocus={(e) => {
          e.target.style.border = `2px solid ${accessibilityColors.theme.blueHover}`
        }}
        onBlur={(e) => {
          if (isActive) {
            e.target.style.border = `1px solid #2F4151`
            e.target.style.borderBottom = `1px solid ${white}`
          } else {
            e.target.style.border = 'none'
          }
        }}
        {...restProps}
      >
        {disabled && (
          <ScreenReaderOnly id={id || `${dataCy}-tab-control`}>
            {tabDisabledTitle} disabled
          </ScreenReaderOnly>
        )}
        {icon}
        <span className="header-tab-label">
          {linkLabel}
          {isNew && (
            <>
              {' '}
              <StyledSup>NEW</StyledSup>
            </>
          )}
        </span>
        {tabIcon}
      </TabWrapper>
    </Tooltip>
  )
}

HeaderTabs.propTypes = {
  id: PropTypes.string.isRequired,
  to: PropTypes.string,
  disabled: PropTypes.bool.isRequired,
  dataCy: PropTypes.string.isRequired,
  isActive: PropTypes.bool.isRequired,
  linkLabel: PropTypes.any.isRequired,
  icon: PropTypes.any.isRequired,
  onClickHandler: PropTypes.func,
  activeStyle: PropTypes.object,
  isNew: PropTypes.bool,
}

HeaderTabs.defaultProps = {
  to: '#',
  onClickHandler: () => {},
  activeStyle: {},
  isNew: false,
}

export default withRouter(HeaderTabs)

export const StyledTabs = styled.div`
  min-width: 300px;
  display: flex;
  align-items: center;
  align-self: flex-end;
  justify-content: center;

  @media (min-width: ${largeDesktopWidth}) {
    min-width: 480px;
    padding-left: 0px;
  }

  @media (min-width: ${mediumDesktopExactWidth}) {
    min-width: 500px;
  }
  @media (max-width: ${tabletWidth}) {
    min-width: fit-content;
  }
`

export const TabWrapper = styled.div.attrs((props) => ({
  as: props.disabled ? 'span' : 'button',
}))`
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  font-size: 11px;
  font-weight: 600;
  color: ${(props) =>
    props.isActive ? '#2F4151' : accessibilityColors.tabs.grey.text};
  border: 1px solid
    ${(props) =>
      props.isActive ? '#2f4151' : accessibilityColors.tabs.grey.bg};
  border-bottom-color: ${(props) => props.isActive && white};
  width: auto;
  padding: 0px 17px;
  text-align: center;
  height: ${(props) => (props.isActive ? '43px' : '42px')};
  margin: 0 2px;
  margin-bottom: ${(props) => (props.isActive ? '-1px' : '0px')};
  border-radius: 4px 4px 0px 0px;
  background-color: ${(props) =>
    props.isActive ? white : accessibilityColors.tabs.grey.bg};
  position: relative;
  ${({ tabIcon }) => tabIcon && 'position: relative;'}
  ${({ style }) => style};
  ${({ disabled }) =>
    disabled ? 'opacity: 0.4; cursor: not-allowed;' : 'cursor: pointer;'};
  ${(props) => props.isActive && props.activeStyle}
  svg {
    fill: ${(props) =>
      props.isActive ? '#2F4151' : accessibilityColors.tabs.grey.text};
    display: ${({ hideIcon }) => hideIcon && 'none'};
    &:hover {
      fill: ${(props) =>
        props.isActive ? '#2F4151' : accessibilityColors.tabs.grey.text};
    }
  }
  .header-tab-label {
    padding: 0px;
    white-space: nowrap;

    @media (max-width: ${smallDesktopWidth}) {
      display: ${({ hasIcon }) => (hasIcon ? 'none' : '')};
    }

    @media (min-width: ${({ mediumDesktopWidth }) => mediumDesktopWidth}) {
      padding: ${({ hasIcon }) => (hasIcon ? '0 15px 0 12px' : '0 15px')};
    }
    @media (max-width: ${desktopWidth}) {
      white-space: wrap;
    }
  }

  &:focus {
    outline: 4px solid ${accessibilityColors.theme.blueHover} !important;
  }

  @media (min-width: ${mediumDesktopExactWidth}) {
    padding: 0px 19px;
    svg {
      display: flex;
    }
  }

  @media (max-width: ${smallDesktopWidth}) and (min-width: ${tabletWidth}) {
    padding: 0px 24px;
    height: ${(props) => (props.isActive ? '36px' : '35px')};
    & svg {
      display: block;
      margin-right: ${({ hasIcon }) => (hasIcon ? '0px' : '')};
    }
  }

  @media (max-width: ${mobileWidthMax}) {
    flex-basis: 100%;
  }
`

export const StyledSup = styled.sup`
  color: ${accessibilityColors.tabs.newTag};
  font-size: 8px;
  font-weight: bold;
`
